<template>
  <transition
    :enterActiveClass="reducedMotionEntryTransition"
    @afterEnter="onAnimationEnd"
    @enterCancelled="cancelled"
  >
    <template v-if="isIntersecting">
      <slot v-bind="$attrs" />
    </template>
  </transition>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  PropType,
  ref,
  watchEffect,
  watch,
  inject
} from "vue";
import { injectIntersectionObserver } from "../hooks/useIntersectionObserver";
import { Button } from "@fresh8/types/src";
import { createUseStyles } from "@fresh8/ad-data-composition/src/theme";
import {
  BrandContext,
  injectBrand
} from "@fresh8/ad-data-composition/src/brand";
import { injectAnimationSync } from "../hooks/useAnimationSync";

export default defineComponent({
  name: "ButtonTransition",
  props: {
    buttonId: {
      type: String,
      required: true
    },
    ignoreSync: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const elref = ref<Element | null>(null);

    const branding = injectBrand() || ({} as BrandContext);
    const stagger = inject("button-transition-stagger", 0);
    const isAnimationDisabled = inject("button-transition-disable", false);

    const { synced, listener } = injectAnimationSync();

    const entry = computed(() => {
      if (isAnimationDisabled) return "none";
      return (
        branding?.buttons?.value?.[props.buttonId]?.animations?.entry || "none"
      );
    });

    const repeat = computed(() => {
      if (isAnimationDisabled) return "none";
      return (
        branding?.buttons?.value?.[props.buttonId]?.animations?.repeat || "none"
      );
    });

    watch([entry, repeat], () => {
      isIntersecting.value = false;

      queueMicrotask(() => {
        isIntersecting.value = true;
      });
    });

    const observer = injectIntersectionObserver();

    const isIntersecting = ref(isNone(entry.value) && isNone(repeat.value));

    const { classesRef: classes } = useStyles(ref({ stagger: stagger }));

    // util
    function isNone(value: string) {
      return value === "none";
    }

    const reducedMotionEntryTransition = computed<string>(() => {
      if (window?.matchMedia) {
        return window?.matchMedia(`(prefers-reduced-motion: reduce)`)
          .matches === true
          ? "none"
          : classes.value[entry.value];
      }

      return "none";
    });

    // effect to make sure animation runs if observer is loaded with add already on screen, (so it actually triggers a transition)
    watchEffect(() => {
      if (
        isIntersecting.value === false &&
        observer.value.isIntersecting === true
      ) {
        queueMicrotask(() => {
          isIntersecting.value = !!observer.value.isIntersecting;
        });
      }
    });

    // Animation Syncing

    watch(synced, sync => {
      if (sync && elref.value) {
        elref.value.classList.add(
          classes.value.animation,
          classes.value[repeat.value],
          classes.value.infinite
        );
      }
    });

    function onAnimationEnd(el: Element) {
      elref.value = el;

      if (isIntersecting.value) {
        if (isNone(entry.value) && !isNone(repeat.value)) {
          elref.value.classList.add(
            classes.value.animation,
            classes.value[repeat.value],
            classes.value.infinite
          );
        }

        if (!isNone(entry.value)) {
          listener.call(null);
        }
      }
    }

    return {
      onAnimationEnd,
      cancelled: () => {
        listener.call(null);
      },
      reducedMotionEntryTransition,
      isIntersecting
    };
  }
});

const { useStyles } = createUseStyles(
  {
    animation: {
      animationDuration: "1s",
      animationFillMode: "both"
    },
    wait: {
      animationDelay: "1s"
    },
    infinite: {
      animationIterationCount: "infinite"
    },
    "@keyframes bounce": {
      "from,20%,53%": {
        animationTimingFunction: "cubic-bezier(0.215, 0.61, 0.355, 1)",
        transform: "translate3d(0, 0, 0)"
      },

      "40%,43%": {
        animationTimingFunction: "cubic-bezier(0.755, 0.05, 0.855, 0.06)",
        transform: "translate3d(0, -6px, 0) scaleY(1.04)"
      },

      "70%": {
        animationTimingFunction: "cubic-bezier(0.755, 0.05, 0.855, 0.06)",
        transform: "translate3d(0, -4px, 0) scaleY(1.03)"
      },

      "80%": {
        animationTimingFunction: "cubic-bezier(0.215, 0.61, 0.355, 1)",
        transform: "translate3d(0, 0, 0) scaleY(0.95)"
      },

      "90%": {
        transform: "translate3d(0, -2px, 0) scaleY(0.98)"
      }
    },
    bounce: {
      animationName: "$bounce",
      transformOrigin: "center bottom",
      animationFillMode: "both"
    },
    "@keyframes pulse": {
      from: {
        transform: "scale3d(1, 1, 1)"
      },
      "50%": {
        transform: "scale3d(0.98, 0.98, 0.6)"
      },
      to: {
        transform: "scale3d(1, 1, 1)"
      }
    },
    pulse: {
      animationName: "$pulse",
      animationTimingFunction: "ease-in-out"
    },
    "@keyframes rubberBand": {
      from: {
        transform: "scale3d(1, 1, 1)"
      },
      "30%": {
        transform: "scale3d(1.012, 0.972, 1.2)"
      },
      "40%": {
        transform: "scale3d(0.982, 1.01, 1)"
      },
      "50%": {
        transform: "scale3d(1.005, 0.995, 1)"
      },
      "65%": {
        transform: "scale3d(0.99, 1.005, 1)"
      },
      "75%": {
        transform: "scale3d(1.003, 0.995, 1)"
      },
      to: {
        transform: "scale3d(1, 1, 1)"
      }
    },
    rubberBand: {
      animationName: "$rubberBand",
      animationDuration: "1s"
    },
    "@keyframes headShake": {
      "0%": {
        transform: "translateX(0) rotate3d(0)"
      },
      "6.5%": {
        transform: "translateX(-2px) rotate3d(0, 1, 0, 4deg)"
      },
      "18.5%": {
        transform: "translateX(2px) rotate3d(0, 1, 0, -4deg)"
      },
      "31.5%": {
        transform: "translateX(-1px) rotate3d(0, 1, 0, 2deg)"
      },
      "43.5%": {
        transform: "translateX(1px) rotate3d(0, 1, 0, -5deg)"
      },
      "50%": {
        transform: "translateX(0) rotate3d(0)"
      }
    },
    headShake: {
      animationTimingFunction: "ease-in-out",
      animationName: "$headShake"
    },
    "@keyframes heartBeat": {
      "0%": {
        transform: "scale3d(1, 1, 1)"
      },
      "14%": {
        transform: "scale3d(1.03, 1.03, 1.9)"
      },
      "28%": {
        transform: "scale3d(1, 1, 1)"
      },
      "42%": {
        transform: "scale3d(1.025, 1.025, 1.4)"
      },
      "70%": {
        transform: "scale3d(1, 1, 1)"
      }
    },
    heartBeat: {
      animationName: "$heartBeat",
      animationFillMode: "both",
      animationTimingFunction: "ease-in-out"
    },
    "@keyframes bounceIn": {
      "from,20%,40%,60%,80%,to": {
        animationTimingFunction: "cubic-bezier(0.215, 0.61, 0.355, 1)"
      },
      "0%": {
        opacity: 0,
        transform: "scale3d(0.96, 0.96, 0.96)"
      },
      "20%": {
        transform: "scale3d(1.01, 1.01, 1.01)"
      },
      "40%": {
        opacity: 1,
        transform: "scale3d(0.98, 0.98, 0.98)"
      },
      "60%": {
        transform: "scale3d(1.01, 1.01, 1.01)"
      },
      to: {
        transform: "scale3d(1, 1, 1)"
      }
    },
    bounceIn: {
      animationDelay: props => `calc(${props.stagger} * 100ms)`,
      animationFillMode: "both",
      animationDuration: "2s",
      animationName: "$bounceIn"
    },
    "@keyframes flipX": {
      from: {
        transform: "perspective(400px) rotate3d(1, 0, 0, 90deg)",
        animationTimingFunction: "ease-in",
        opacity: 0
      },
      "40%": {
        transform: "perspective(400px) rotate3d(1, 0, 0, -20deg)"
      },
      "60%": {
        transform: "perspective(400px) rotate3d(1, 0, 0, 5deg)",
        opacity: 1
      },
      "80%": {
        transform: "perspective(400px) rotate3d(1, 0, 0, -1deg)"
      },
      to: {
        transform: "perspective(400px) rotate3d(1, 0, 0, 0deg)"
      }
    },
    flipX: {
      animationDelay: props => `calc(${props.stagger} * 100ms)`,
      animationDuration: "2s",
      animationFillMode: "both",
      //@ts-ignore
      backfaceVisibility: "visible !important",
      animationName: "$flipX"
    },
    "@media (prefers-reduced-motion: reduce)": {
      "*": {
        animation: "none !important",
        transitionDuration: "0s !important"
      }
    }
  },
  { stagger: 0 }
);
</script>
